(() => {
  if (customElements.get('lk-cart-price')) return;
  /* 5 % Rabatt ab €100 Warenkorbwert (Gesamtwarenkorb inkl. dieses Artikels).
     Darunter zeigt die Zeile, ab wie vielen Stück der Rabatt greift und den Stückpreis dann. */
  const THRESHOLD = 10000;
  const RATE = 5;
  const TEXT = {
    de: { label: 'Dein Warenkorbpreis', from: 'Warenkorbpreis ab {n} Stück', piece: '/ Stück', ok: '5 % Rabatt · automatisch abgezogen', okMulti: '5 % Rabatt · {unit} pro Stück', hint: '5 % Rabatt ab €100 Warenkorbwert · antippen für {n} Stück' },
    en: { label: 'Your cart price', from: 'Cart price from {n} pieces', piece: '/ piece', ok: '5 % off · deducted automatically', okMulti: '5 % off · {unit} per piece', hint: '5 % off from €100 cart value · tap for {n} pieces' },
    fr: { label: 'Ton prix panier', from: 'Prix panier dès {n} pièces', piece: '/ pièce', ok: '5 % de remise · déduite automatiquement', okMulti: '5 % de remise · {unit} la pièce', hint: '5 % de remise dès 100 € de panier · touche pour {n} pièces' },
    es: { label: 'Tu precio en la cesta', from: 'Precio en la cesta desde {n} uds.', piece: '/ ud.', ok: '5 % de descuento · aplicado automáticamente', okMulti: '5 % de descuento · {unit} por unidad', hint: '5 % de descuento desde 100 € · toca para {n} uds.' }
  };
  const fill = (s, map) => s.replace(/\{(\w+)\}/g, (_, k) => map[k] ?? '');
  const discounted = (cents) => cents - Math.round(cents * RATE / 100);

  class LKCartPrice extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.root = this.closest('.lk-product-page');
      this.form = document.getElementById(this.dataset.form);
      this.source = this.form?.querySelector('[name="add"]');
      if (!this.root || !this.source) return;
      this.abort = new AbortController();
      this.variants = JSON.parse(this.querySelector('[data-cart-price-variants]').textContent);
      this.button = this.querySelector('button');
      this.label = this.querySelector('[data-cart-price-label]');
      this.amount = this.querySelector('[data-cart-price-amount]');
      this.note = this.querySelector('[data-cart-price-note]');
      this.subtotal = Number(this.dataset.cartSubtotal);
      this.t = TEXT[(this.dataset.lang || 'de').slice(0, 2)] || TEXT.de;
      this.format = new Intl.NumberFormat(this.dataset.lang, { style: 'currency', currency: this.dataset.currency });
      this.variant = this.variants.find(v => String(v.id) === this.idValue());
      const options = { signal: this.abort.signal };
      this.root.addEventListener('input', () => this.update(), options);
      this.root.addEventListener('change', () => {
        this.variant = this.variants.find(v => String(v.id) === this.idValue()) || this.variant;
        this.update();
      }, options);
      document.addEventListener(`variant:change:${this.root.dataset.sectionId}:${this.root.dataset.productId}`, ({ detail }) => {
        this.variant = detail.variant;
        this.update();
      }, options);
      this.button.addEventListener('click', () => {
        this.update();
        if (this.button.disabled) return;
        if (this.needed > this.quantity) {
          this.setQuantity(this.needed);
          return;
        }
        this.source.click();
        this.update();
      }, options);
      this.observer = new MutationObserver(() => {
        const busy = this.isBusy();
        if (this.wasBusy && !busy) this.refreshCart();
        this.wasBusy = busy;
        this.update();
      });
      this.observer.observe(this.source, { attributes: true, attributeFilter: ['disabled', 'aria-busy', 'class'] });
      for (const event of ['ajaxProduct:added', 'cart:updated', 'cart:refresh']) document.addEventListener(event, () => this.refreshCart(), options);
      this.update();
    }
    idValue() { return this.form.querySelector('[name="id"]')?.value; }
    quantityInput() { return this.root.querySelector('input[name="quantity"]'); }
    isBusy() { return this.source.getAttribute('aria-busy') === 'true' || this.source.classList.contains('btn--loading'); }
    setQuantity(n) {
      const input = this.quantityInput();
      if (!input) return;
      input.value = String(n);
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      this.update();
    }
    update() {
      const input = this.quantityInput();
      const quantity = Number(input?.value ?? 1);
      const valid = Number.isInteger(quantity) && quantity > 0 && (!input || input.validity.valid) && !!this.variant;
      const price = Math.round(Number(this.variant?.price || 0));
      const line = price * quantity;
      const subtotal = Number.isFinite(this.subtotal) ? this.subtotal : 0;
      const eligible = valid && subtotal + line >= THRESHOLD;
      /* Stückzahl, ab der der Gesamtwarenkorb €100 erreicht */
      const needed = price > 0 ? Math.max(1, Math.ceil(Math.max(0, THRESHOLD - subtotal) / price)) : 1;
      this.quantity = quantity;
      this.needed = eligible ? quantity : needed;

      const busy = this.isBusy();
      this.button.disabled = this.source.disabled || busy || !valid;
      this.button.setAttribute('aria-busy', String(busy));

      if (!valid) {
        this.amount.textContent = '—';
        return;
      }
      if (eligible) {
        const total = discounted(line);
        if (this.label) this.label.textContent = this.t.label;
        this.amount.textContent = this.format.format(total / 100);
        this.note.textContent = quantity > 1
          ? fill(this.t.okMulti, { unit: this.format.format(discounted(price) / 100) })
          : this.t.ok;
      } else {
        if (this.label) this.label.textContent = fill(this.t.from, { n: needed });
        this.amount.textContent = `${this.format.format(discounted(price) / 100)} ${this.t.piece}`;
        this.note.textContent = fill(this.t.hint, { n: needed });
      }
    }
    async refreshCart() {
      if (this.refreshPending) return;
      this.refreshPending = true;
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' }, signal: this.abort.signal });
        if (response.ok) { this.subtotal = (await response.json()).original_total_price; this.update(); }
      } catch (_) {} finally { this.refreshPending = false; }
    }
    disconnectedCallback() { this.abort?.abort(); this.observer?.disconnect(); this.ready = false; }
  }
  customElements.define('lk-cart-price', LKCartPrice);
})();
